import Grid from '@mui/material/Grid';
import { DrawerActionBar } from '@openx/components-core/lib/Drawer/DrawerActionBar';
import { TextField } from '@openx/components-core/lib/TextField/TextField';
import { InvalidItems } from '@openx/components-core/lib/TransferList/bulk/InvalidItems/InvalidItems';
import { DomainTargeting, type UrlTargeting, UrlTargetingType } from '@openx/types/targeting/domains';
import { noop } from '@openx/utils/noop';
import isEqual from 'lodash/isEqual';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NEWLINE_REGEX } from '../../Domains/constants';
import type { ValidationResult } from '../BulkAdd/types';
import { useBulkReducer } from './utils/useBulkReducer';
import { useGetActions } from './utils/useGetActions';

type TextFieldEditorProps = {
  validatedTextInput: string;
  onApply: (items: string[]) => void;
  errorText?: string;
  children?: ReactNode;
  placeholder: string;
  helperText?: string;
  validate: (value: string[], inventoryListItems?: string[]) => ValidationResult;
  limit?: number;
  maxRows?: number;
  handleReset?: () => UrlTargeting;
  shouldDisplayResetButton?: boolean;
  isResetToAccountEnabled?: boolean;
  inventoryListItems?: string[];
  inventoryListId?: string | null;
  setApplyEnabled?: (value: boolean) => void;
  isBlacklist: boolean;
};

export const TextFieldEditor = ({
  validatedTextInput,
  onApply,
  errorText,
  children,
  placeholder,
  helperText,
  validate,
  limit = Number.MAX_SAFE_INTEGER,
  inventoryListItems,
  maxRows,
  handleReset = () => ({
    type: UrlTargetingType.whitelist,
    domain_targeting_option: DomainTargeting.ALL_VARIATIONS,
    urls: undefined,
  }),
  shouldDisplayResetButton = false,
  isResetToAccountEnabled = false,
  inventoryListId,
  setApplyEnabled = noop,
  isBlacklist,
}: TextFieldEditorProps) => {
  const { t } = useTranslation();

  const { bulk, onChange, onReset, onValidate } = useBulkReducer({
    inventoryListItems,
    onApply,
    validate,
    handleReset,
    validatedTextInput,
    setApplyEnabled,
    isBlacklist,
  });

  const isResetEnabled = isResetToAccountEnabled || bulk.value !== validatedTextInput;

  const useGetActionsProps = useMemo(
    () =>
      shouldDisplayResetButton
        ? {
            shouldDisplayResetButton,
            bulk: {
              isValidating: bulk.isValidating,
              isResetEnabled,
            },
            onReset,
            onValidate,
          }
        : {
            shouldDisplayResetButton,
            bulk: {
              isValidating: bulk.isValidating,
            },
            onValidate,
          },
    [shouldDisplayResetButton, bulk.isValidating, isResetEnabled, onReset, onValidate]
  );

  const actions = useGetActions(useGetActionsProps);
  const inventoryListRef = useRef(inventoryListItems);

  useEffect(() => {
    const hasInventoryListChanged = !isEqual(inventoryListRef.current, inventoryListItems);

    if (inventoryListId && hasInventoryListChanged) {
      onValidate(hasInventoryListChanged);
      inventoryListRef.current = inventoryListItems;
    }
  }, [inventoryListId, inventoryListItems, onValidate]);

  const noPlaceholderLines = useMemo(() => placeholder.split(NEWLINE_REGEX).length, [placeholder]);

  const isLimitReached = bulk.validItems.length > limit;

  return (
    <>
      <Grid
        sx={{
          '.MuiPaper-root.MuiAlert-standard': {
            alignItems: 'center',
            width: '100%',
          },
        }}
      >
        {!!bulk.errors.length && <InvalidItems errors={bulk.errors} errorText={errorText} />}
      </Grid>

      {children}

      <Grid
        container
        sx={{
          marginBottom: theme => theme.spacing(1),
        }}
      >
        <TextField
          dataTest="targeting-bulk-input"
          multiline
          fullWidth
          slotProps={{ input: { disableUnderline: true } }}
          helperText={isLimitReached ? t('The limit has been reached ({limit}).', { limit }) : helperText}
          error={isLimitReached}
          placeholder={placeholder}
          onChange={onChange}
          value={bulk.value}
          disabled={bulk.isValidating}
          maxRows={maxRows ?? noPlaceholderLines}
          minRows={noPlaceholderLines}
        />
      </Grid>

      <DrawerActionBar
        actions={actions}
        gutterBottom
        hideDivider
        backgroundColor="inherit"
        dataTest="bulk-drawer-buttons"
      />
    </>
  );
};
